import axios from "axios";
import { getUsersForRole } from "./user-service";
import { decryptData } from "../utils/encryption";

const TRANS_URL = process.env.REACT_APP_TRANSACTION_API_URL;

// Function to get user names by role code and division
const getUserNamesByRoleAndDivision = async (roleCode, division, location) => {
  try {
    const users = await getUsersForRole(roleCode);
    const filteredUsers = users.filter(user => {
      if (!user.DeleteIndication) return false;
      if (division && user.Division !== division) return false;
      if (location) {
        const userLocations = user.Location ? user.Location.split(",").map(loc => loc.trim()) : [];
        if (!userLocations.includes(location)) return false;
      }
      return true;
    });
    // Decrypt user names before returning
    return filteredUsers.map(user => decryptData(user.UserName));
  } catch (error) {
    console.error("Error fetching users for role:", error);
    return [];
  }
};

// Function to resolve pending with names from role codes
const resolvePendingWithNames = async (data) => {
  const resolvedData = await Promise.all(
    data.map(async (item) => {
      if (item.PendingWith) {
        const pendingWithValues = item.PendingWith.includes(',')
          ? item.PendingWith.split(',').map(val => val.trim())
          : [item.PendingWith];

        const allUserNames = [];
        for (const roleCode of pendingWithValues) {
          const userNames = await getUserNamesByRoleAndDivision(roleCode, item.InitiatorDivision, item.InitiatorLocation);
          allUserNames.push(...(userNames.length > 0 ? userNames : [roleCode]));
        }

        return {
          ...item,
          PendingWith: allUserNames
        };
      }
      return item;
    })
  );
  return resolvedData;
};

export async function getPendingData(userRole, userDivision, userLocation, userName) {
  // Only show pending approvals for users with approval roles
  if (userRole !== "DIVAPPROVER" && userRole !== "FINAPPROVER" && userRole !== "ADMIN") {
    return [];
  }

  const response = await axios
    .get(TRANS_URL + "/disposalintimationlist/getdisposaldata")
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      console.log("error => ", error);
      return [];
    });

  const data = Array.isArray(response.data.data) ? response.data.data : [];

  // Filter data for open workflow statuses
  const openStatuses = ["Pending for Divisional Approval", "Pending for Final Approval"];
  const filteredData = data.filter(item => {
    if (!openStatuses.includes(item.Status)) return false;

    if (userRole === "DIVAPPROVER") {
      if (item.Status !== "Pending for Divisional Approval" || item.InitiatorDivision !== userDivision) {
        return false;
      }

      // Check location match for DIVAPPROVER
      const userLocations = userLocation ? userLocation.split(",").map(loc => loc.trim()) : [];
      const itemLocation = item.InitiatorLocation === "Other" ? item.IfOtherLoc : item.InitiatorLocation;
      return userLocations.includes(itemLocation);
    } else if (userRole === "FINAPPROVER" || userRole === "ADMIN") {
      return item.Status === "Pending for Final Approval";
    }
    return false;
  });

  // Set PendingWith to current user's name only
  const resolvedData = filteredData.map(item => ({
    ...item,
    PendingWith: [userName]
  }));

  return resolvedData;
}

export async function getSentForApprovalData(userId, userRole, userDivision, userLocation) {
  const response = await axios
    .get(TRANS_URL + "/disposalintimationlist/getdisposaldata")
    .then(function (resp) {
      return resp;
    })
    .catch(function (error) {
      console.log("error => ", error);
      return [];
    });

  const data = Array.isArray(response.data.data) ? response.data.data : [];

  // Filter data for open workflow statuses only
  const openStatuses = ["Pending for Divisional Approval", "Pending for Final Approval", "Rework"];
  const filteredData = data.filter(item => {
    if (!openStatuses.includes(item.Status)) return false;

    // Show only forms created by current user OR forms where user is related
    if (item.CreatedBy === userId) return true;

    // For DIVAPPROVER role, show forms from same division and location
    if (userRole === "DIVAPPROVER") {
      if (item.InitiatorDivision !== userDivision) return false;
      const userLocations = userLocation ? userLocation.split(",").map(loc => loc.trim()) : [];
      const itemLocation = item.InitiatorLocation === "Other" ? item.IfOtherLoc : item.InitiatorLocation;
      return userLocations.includes(itemLocation);
    }

    // For FINAPPROVER and ADMIN, show all forms that reach final approval stage
    if (userRole === "FINAPPROVER" || userRole === "ADMIN") {
      return item.Status === "Pending for Final Approval";
    }

    return false;
  });

  // Resolve pending with names from role codes
  const resolvedData = await resolvePendingWithNames(filteredData);
  return resolvedData;
}
